import { BASE_URL } from '../../../shared/lib/api';
import type { TimeWindow } from './dashboardApi';

/**
 * Ticker market data response
 * 
 * Single ticker: { data: TickerMarketData, meta: {...} }
 * Batch: { data: { tickers: Record<string, TickerMarketData> }, meta: {...} }
 */
export interface TickerMarketResponse {
  data: TickerMarketData | TickerMarketBatchResponse;
  meta: {
    as_of_date: string;
    refreshed_at: string;
  };
}

export interface TickerMarketBatchResponse {
  tickers: Record<string, TickerMarketData>;
}

export interface TickerMarketData {
  ticker: string;
  company_name: string;
  last_price: number;
  day_pct_change: number;
  history_percentage: Record<string, number>; // { "1m": 5.2, "3m": 8.1, ... }
  as_of_date: string;
  updated_at: string;
}

/**
 * Ticker chart data response (shared market price series)
 */
export interface TickerChartResponse {
  data: {
    tickerCharts: TickerChartData; // { "AAPL": { "6m": [...] } }
  };
  meta: {
    as_of_date: string;
    refreshed_at: string;
    time_window: string;
  };
}

export interface TickerChartPoint {
  date: string;
  price: number;
}

export interface TickerChartData {
  [ticker: string]: {
    [window: string]: TickerChartPoint[];
  };
}

/**
 * User ticker chart data response (user-specific cost basis and PnL)
 */
export interface UserTickerChartResponse {
  data: {
    account_id: string;
    ticker: string;
    window: string;
    series: UserTickerChartPoint[];
  };
  meta: {
    as_of_date: string;
    refreshed_at: string;
    time_window: string;
  };
}

export interface UserTickerChartPoint {
  date: string;
  cost_basis: number;
  cumulative_dollar_pnl: number;
}

/** One row from `GET /api/market/symbols/search` (local ticker_directory autocomplete). */
export interface TickerDirectorySearchResult {
  ticker: string;
  company_name: string | null;
  exchange: string | null;
  asset_type: string | null;
}

/**
 * Search the local ticker directory for autocomplete suggestions.
 * Never hits yfinance — results come from the seeded `ticker_directory` table.
 */
export async function searchTickerDirectory(params: {
  query: string;
  limit?: number;
  token: string;
}): Promise<TickerDirectorySearchResult[]> {
  const limit = params.limit ?? 10;
  const url =
    `${BASE_URL}/market/symbols/search` +
    `?q=${encodeURIComponent(params.query)}` +
    `&limit=${encodeURIComponent(String(limit))}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    credentials: 'include',
  });

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error(`searchTickerDirectory failed: ${res.status}`);
  }

  return res.json() as Promise<TickerDirectorySearchResult[]>;
}

/**
 * Get ticker market data (single ticker)
 */
export async function getTickerMarket(params: {
  ticker: string;
  token: string;
}): Promise<TickerMarketResponse> {
  const res = await fetch(`${BASE_URL}/ticker/market?ticker=${encodeURIComponent(params.ticker)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    credentials: 'include',
  });

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error(`getTickerMarket failed: ${res.status}`);
  }

  return res.json() as Promise<TickerMarketResponse>;
}

/**
 * Get ticker market data (batch - multiple tickers)
 */
export async function getTickerMarketBatch(params: {
  tickers: string[];
  token: string;
}): Promise<TickerMarketResponse> {
  const tickersParam = params.tickers.map(t => encodeURIComponent(t)).join(',');
  const res = await fetch(`${BASE_URL}/ticker/market?tickers=${tickersParam}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    credentials: 'include',
  });

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error(`getTickerMarketBatch failed: ${res.status}`);
  }

  return res.json() as Promise<TickerMarketResponse>;
}

/**
 * Get shared ticker chart data (market price series)
 */
export async function getTickerChart(params: {
  ticker: string;
  window: TimeWindow;
  token: string;
}): Promise<TickerChartResponse> {
  const res = await fetch(
    `${BASE_URL}/ticker/chart?ticker=${encodeURIComponent(params.ticker)}&window=${params.window}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.token}`,
      },
      credentials: 'include',
    }
  );

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error(`getTickerChart failed: ${res.status}`);
  }

  return res.json() as Promise<TickerChartResponse>;
}

/**
 * Get user-specific ticker chart data (cost basis and PnL)
 */
export async function getUserTickerChart(params: {
  accountId: string;
  ticker: string;
  window: TimeWindow;
  token: string;
}): Promise<UserTickerChartResponse> {
  const res = await fetch(
    `${BASE_URL}/ticker/user/chart?account_id=${encodeURIComponent(params.accountId)}&ticker=${encodeURIComponent(params.ticker)}&window=${params.window}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.token}`,
      },
      credentials: 'include',
    }
  );

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (res.status === 404) {
    throw new Error('Account not found or access denied');
  }
  if (!res.ok) {
    throw new Error(`getUserTickerChart failed: ${res.status}`);
  }

  return res.json() as Promise<UserTickerChartResponse>;
}
